import { Ban, Flag, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../api/client'
import { trustApi } from '../../api/endpoints'
import { ErrorMessage } from '../ErrorMessage'
import type { TrustReportCategory } from '../../types/api'

/** In the order a person scanning the list is most likely to want them. */
const CATEGORIES: { value: TrustReportCategory; label: string }[] = [
  { value: 'SPAM', label: 'Spam' },
  { value: 'FRAUD', label: 'Fraud / scam' },
  { value: 'INAPPROPRIATE', label: 'Inappropriate behaviour' },
  { value: 'IMPERSONATION', label: 'Pretending to be someone else' },
  { value: 'SAFETY', label: 'Safety concern' },
  { value: 'MINOR_SAFETY', label: 'Child safety concern' },
  { value: 'FULFILMENT', label: 'Did not deliver what was promised' },
  { value: 'OTHER', label: 'Other' },
]

/** Categories where blocking is almost always what the reporter wants too, so it starts ticked. */
const BLOCK_BY_DEFAULT: TrustReportCategory[] = ['SAFETY', 'MINOR_SAFETY', 'SPAM']

const DETAILS_MAX = 1000

type Mode = 'report' | 'block'

/**
 * A low-emphasis "Report or block" control for anywhere one person looks at another. Safety
 * tools, not primary actions: a quiet text button that opens its own dialog, so it can sit inside
 * another dialog (the map's provider card) without competing with Connect.
 */
export function ReportBlockActions({
  participantId,
  name,
  connectionRequestId,
  onBlocked,
  variant = 'text',
}: {
  participantId: string
  /** How to refer to them - "Asha Rao", or "this learner" when the name is not shared yet. */
  name: string
  /** The request the report is about, when there is one - helps the team review it. */
  connectionRequestId?: string
  /** Called once a block has gone through and the dialog is closed. */
  onBlocked?: () => void
  /** "text" shows the words; "icon" is a flag alone, for tight rows. */
  variant?: 'text' | 'icon'
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Report or block ${name}`}
        title="Report or block"
        className={
          variant === 'icon'
            ? 'grid h-7 w-7 place-items-center rounded-full text-ink-400 transition hover:bg-ink-100 hover:text-ink-700'
            : 'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-ink-400 transition hover:bg-ink-100 hover:text-ink-700'
        }
      >
        <Flag size={variant === 'icon' ? 14 : 12} />
        {variant === 'text' && <span>Report or block</span>}
      </button>
      {open && (
        <ReportBlockDialog
          participantId={participantId}
          name={name}
          connectionRequestId={connectionRequestId}
          onClose={(blocked) => {
            setOpen(false)
            // Only now, not the moment the block lands: the caller usually reloads or closes the
            // card, which would unmount this dialog before the person read the confirmation.
            if (blocked) onBlocked?.()
          }}
        />
      )}
    </>
  )
}

function ReportBlockDialog({
  participantId,
  name,
  connectionRequestId,
  onClose,
}: {
  participantId: string
  name: string
  connectionRequestId?: string
  onClose: (blocked: boolean) => void
}) {
  const [mode, setMode] = useState<Mode>('report')
  const [category, setCategory] = useState<TrustReportCategory | ''>('')
  const [details, setDetails] = useState('')
  const [alsoBlock, setAlsoBlock] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set once something went through; the dialog then shows only the confirmation.
  const [done, setDone] = useState<string | null>(null)
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onClose(blocked)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [saving, blocked, onClose])

  function pickCategory(value: TrustReportCategory | '') {
    setCategory(value)
    setAlsoBlock(value !== '' && BLOCK_BY_DEFAULT.includes(value))
  }

  async function submitReport() {
    if (!category) {
      setError('Choose what this is about.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      await trustApi.report({
        subjectParticipantId: participantId,
        category,
        details: details.trim() || undefined,
        connectionRequestId,
      })
      if (alsoBlock) {
        await trustApi.block({ blockedParticipantId: participantId })
        setBlocked(true)
      }
      setDone(
        alsoBlock
          ? `Thanks - the YellowDots team will review this. You have also blocked ${name}.`
          : 'Thanks - the YellowDots team will review this.',
      )
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send your report'))
    } finally {
      setSaving(false)
    }
  }

  async function submitBlock() {
    setError(null)
    setSaving(true)
    try {
      await trustApi.block({ blockedParticipantId: participantId })
      setBlocked(true)
      setDone(`You have blocked ${name}. You can unblock them from your Connections page.`)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not block this person'))
    } finally {
      setSaving(false)
    }
  }

  const tab = (m: Mode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === m}
      onClick={() => {
        setMode(m)
        setError(null)
      }}
      className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
        mode === m ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-800'
      }`}
    >
      {label}
    </button>
  )

  return (
    // Above the map's own dialogs (z-[1100]), since this one usually opens from inside them.
    <div
      className="fixed inset-0 z-[1200] grid place-items-center overflow-y-auto bg-ink-900/50 p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-block-title"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose(blocked)}
    >
      <div className="card-pad max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto bg-white">
        <div className="flex items-start justify-between gap-3">
          <h2 id="report-block-title" className="min-w-0 break-words font-bold text-ink-900">
            Report or block {name}
          </h2>
          <button
            type="button"
            onClick={() => onClose(blocked)}
            disabled={saving}
            aria-label="Close"
            className="text-ink-400 hover:text-ink-700 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <>
            <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{done}</p>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={() => onClose(blocked)} className="btn-ink">
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div role="tablist" className="mt-3 flex gap-1 rounded-xl bg-ink-100 p-1">
              {tab('report', 'Report')}
              {tab('block', 'Block only')}
            </div>

            {mode === 'report' ? (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-ink-500">
                  Reports go to the YellowDots team, not to {name}.
                </p>
                <label className="block">
                  <span className="label">What is this about?</span>
                  <select
                    value={category}
                    onChange={(e) => pickCategory(e.target.value as TrustReportCategory | '')}
                    className="input"
                  >
                    <option value="">Choose one</option>
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="label">Details (optional)</span>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value.slice(0, DETAILS_MAX))}
                    maxLength={DETAILS_MAX}
                    rows={4}
                    className="input"
                    placeholder="What happened, and when?"
                  />
                  <span className="mt-0.5 block text-right text-[11px] text-ink-400">
                    {details.length}/{DETAILS_MAX}
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={alsoBlock}
                    onChange={(e) => setAlsoBlock(e.target.checked)}
                    className="mt-0.5"
                  />
                  Also block this person
                </label>
              </div>
            ) : (
              <p className="mt-3 text-sm text-ink-600">
                Neither of you will be able to send the other a request or see the other&apos;s
                profile and contact details. You can unblock them later from Connections.
              </p>
            )}

            <div className="mt-3">
              <ErrorMessage message={error} />
            </div>

            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={() => onClose(blocked)} disabled={saving} className="btn-ghost">
                Cancel
              </button>
              {mode === 'report' ? (
                <button type="button" onClick={submitReport} disabled={saving} className="btn-ink">
                  <Flag size={14} /> {saving ? 'Sending...' : 'Send report'}
                </button>
              ) : (
                <button type="button" onClick={submitBlock} disabled={saving} className="btn-ink">
                  <Ban size={14} /> {saving ? 'Blocking...' : `Block ${name}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
